import { createComment, deleteComment, updateComment } from "./api";
import { state } from "./app-state";
import { refreshComments } from "./comment-lists";
import { ui } from "./dom";
import { claimModalSelection, closeModal, openModal, setModalBusy } from "./modal";
import { hideSelectionAction } from "./selection-ui";
import { getAuthor, showModalError } from "./session-ui";
import { showToast } from "./toast";
import type { QuoteAnchor, ReviewComment } from "./types";

export function openCommentModal(anchor: QuoteAnchor | null, docId: string): void {
  const isProjectComment = docId === "home";
  openModal({
    kicker: anchor ? "原文批注" : isProjectComment ? "项目反馈" : "当前文档",
    title: anchor ? "提交原文意见" : isProjectComment ? "提交项目总意见" : "提交全文意见",
    label: "你的意见",
    placeholder: "写清楚哪里需要调整，以及你期待的方向……",
    submitText: "提交意见",
    quote: anchor?.text,
    onSubmit: async () => {
      const body = ui.modalBody.value.trim();
      if (!body) {
        ui.modalError.textContent = "请填写意见内容";
        return;
      }
      setModalBusy(true);
      try {
        await createComment({
          doc_id: docId,
          body,
          author_name: getAuthor(),
          ...(anchor ? {
            quote_text: anchor.text,
            quote_prefix: anchor.prefix,
            quote_suffix: anchor.suffix,
          } : {}),
        });
        closeModal();
        hideSelectionAction();
        window.getSelection()?.removeAllRanges();
        await refreshComments();
        showToast("意见已提交", "success");
      } catch (error) {
        showModalError(error, "提交失败");
      } finally {
        setModalBusy(false);
      }
    },
  });
  if (anchor) claimModalSelection();
}

export function openEditModal(comment: ReviewComment): void {
  openModal({
    kicker: "修改意见",
    title: "编辑这条意见",
    label: "意见内容",
    placeholder: "修改你的意见……",
    submitText: "保存修改",
    quote: comment.quote_text || undefined,
    initialValue: comment.body,
    onSubmit: async () => {
      const body = ui.modalBody.value.trim();
      if (!body) {
        ui.modalError.textContent = "请填写意见内容";
        return;
      }
      setModalBusy(true);
      try {
        await updateComment(comment.id, { body, author_name: comment.author_name });
        closeModal();
        await refreshComments();
        showToast("意见已修改，并重新标记为待处理", "success");
      } catch (error) {
        showModalError(error, "修改失败");
      } finally {
        setModalBusy(false);
      }
    },
  });
}

export function openDeleteModal(comment: ReviewComment): void {
  openModal({
    kicker: "删除意见",
    title: "确认删除这条意见？",
    label: "",
    placeholder: "",
    submitText: "确认删除",
    quote: comment.body.slice(0, 240),
    hideInput: true,
    danger: true,
    onSubmit: async () => {
      setModalBusy(true);
      try {
        await deleteComment(comment.id);
        closeModal();
        await refreshComments();
        showToast("意见已删除", "success");
      } catch (error) {
        showModalError(error, "删除失败");
      } finally {
        setModalBusy(false);
      }
    },
  });
}

export function openReplyModal(comment: ReviewComment): void {
  state.replyTarget = comment.id;
  openModal({
    kicker: "管理回复",
    title: "回复这条意见",
    label: "回复内容",
    placeholder: "输入回复；AI 代回可继续使用 [AI] 前缀……",
    submitText: "保存回复",
    quote: comment.body.slice(0, 240),
    initialValue: comment.reply,
    onSubmit: async () => {
      const replyText = ui.modalBody.value.trim();
      if (!replyText) {
        ui.modalError.textContent = "请填写回复内容";
        return;
      }
      setModalBusy(true);
      try {
        await updateComment(comment.id, { reply: replyText });
        closeModal();
        await refreshComments();
        showToast("回复已保存", "success");
      } catch (error) {
        showModalError(error, "回复失败");
      } finally {
        setModalBusy(false);
      }
    },
  });
}
